import { YStack, XStack, Text, Card, View, createContext, Button } from "tamagui";
import { ChevronUp, DollarSign, Info, Percent } from "@tamagui/lucide-icons-2";
import { Controller } from "react-hook-form";
import { Platform } from "react-native-web";
import { CustomSingleCheckboxControlled, VerticalCheckboxesControlled } from "../bento-controlled/forms/checkboxes/VerticalCheckboxes.mjs";
import { InputLabelMessageIconControlled, InputWithLeftIconDemoControlled, InputWithRightIconDemoControlled } from "../bento-controlled/forms/inputs/index.mjs";
import { VerticalRadioGroupControlled } from "../bento-controlled/forms/radiogroups/Vertical.mjs";
import { color } from "@my/util";
import { VerticalRadioGroupWithDescriptionControlled } from "../bento-controlled/forms/radiogroups/VerticalWithDescription.mjs";
import { useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
const generateRegisterWithLabel = register => (labelParam, options) => {
  let label;
  let name;
  if (typeof labelParam === "object") {
    label = labelParam.label;
    name = labelParam.name;
  } else {
    label = labelParam;
    name = labelParam;
  }
  return {
    ...register(name, options),
    label
  };
};
const generateIconInput = type => (controllerOptions, inputOptions) => {
  const InputEle = type === "cash" ? InputWithLeftIconDemoControlled : InputWithRightIconDemoControlled;
  const IconEle = type === "cash" ? DollarSign : Percent;
  return /* @__PURE__ */jsx(Controller, {
    ...controllerOptions,
    render: ({
      field: {
        onChange,
        name,
        value
      },
      fieldState: {
        error
      }
    }) => {
      return /* @__PURE__ */jsx(InputEle, {
        ...{
          onChange,
          name,
          value,
          placeHolder: name,
          label: "Date of Birth",
          icon: IconEle,
          error: !!error,
          ...inputOptions
        }
      });
    }
  });
};
const CheckboxInput = (controllerOptions, inputOptions) => {
  return /* @__PURE__ */jsx(Controller, {
    ...controllerOptions,
    render: ({
      field: {
        onChange,
        name,
        value
      },
      fieldState: {
        error
      }
    }) => {
      return /* @__PURE__ */jsx(CustomSingleCheckboxControlled, {
        ...{
          onCheckedChange: onChange,
          name,
          checked: value,
          placeHolder: name,
          ...inputOptions,
          error: !!error
        }
      });
    }
  });
};
const [QuestionContextProvider, useQuestionContextRaw] = createContext("QuestionCard");
const useQuestionContext = consumerName => {
  try {
    return useQuestionContextRaw(consumerName);
  } catch (e) {
    return void 0;
  }
};
const CustomCheckboxInput = controllerOptions => {
  const {
    options,
    circle = false,
    multiple = false,
    optionDescriptions = false,
    title,
    ...restControllerOptions
  } = controllerOptions;
  const context = useQuestionContext("CustomCheckboxInput");
  const label = typeof context?.showInfo === "boolean" ? context.showInfo ? restControllerOptions.label : "" : restControllerOptions.label;
  if (restControllerOptions.rules?.required === true) {
    const nonBooleanValue = options.find(({
      value
    }) => typeof value !== "boolean");
    if (nonBooleanValue === void 0) {
      delete restControllerOptions.rules.required;
      restControllerOptions.rules.validate = value => typeof value === "boolean";
    }
  }
  const allOption = options.find(({
    value
  }) => value === "all");
  return /* @__PURE__ */jsx(Controller, {
    ...restControllerOptions,
    render: ({
      field: {
        onChange,
        value
      },
      fieldState: {
        error
      }
    }) => {
      const isAllSelected = options.length > 1 && value?.length === options.length - 1;
      const handleChange = optionValue => {
        if (multiple) {
          if (optionValue === "all" && allOption) {
            if (isAllSelected || value?.includes("all")) {
              onChange([]);
            } else {
              onChange(options.map(option => option.value));
            }
          } else {
            if (Array.isArray(value)) {
              const newValue = value.includes(optionValue) ? value.filter(v => v !== optionValue) : [...value, optionValue];
              if (allOption) {
                if (newValue.length === options.length - 1 && !newValue.includes("all")) {
                  onChange([...newValue, "all"]);
                } else {
                  onChange(newValue.filter(v => v !== "all"));
                }
              }
            } else {
              onChange([optionValue]);
            }
          }
        } else {
          onChange(optionValue);
        }
      };
      const radioValueToCheckboxValue = (values = []) => {
        const valuesArray = Array.isArray(values) ? values : typeof values === "string" ? [values] : [];
        return Object.assign({}, ...valuesArray.map(key => ({
          [key]: true
        })));
      };
      const handleCheckboxChange = values => {
        const valuesArray = Object.entries(values).filter(entry => entry[1]).map(entry => entry[0]);
        onChange(valuesArray);
      };
      const radioGroupProps = {
        items: options,
        onChange: handleChange,
        value,
        error,
        ...(label ? {
          label
        } : {})
      };
      return /* @__PURE__ */jsx(View, {
        children: options?.length > 0 ? multiple ? /* @__PURE__ */jsx(VerticalCheckboxesControlled, {
          items: options,
          onChange: handleCheckboxChange,
          values: radioValueToCheckboxValue(value),
          ...(label ? {
            label
          } : {}),
          ...(title ? {
            title
          } : {})
        }) : optionDescriptions ? /* @__PURE__ */jsx(VerticalRadioGroupWithDescriptionControlled, {
          ...radioGroupProps
        }) : /* @__PURE__ */jsx(VerticalRadioGroupControlled, {
          ...radioGroupProps
        }) : /* @__PURE__ */jsx(Text, {
          children: "No options available"
        })
      });
    }
  });
};
const TextInput = (controllerOptions, inputOptions) => {
  return /* @__PURE__ */jsx(Controller, {
    ...controllerOptions,
    render: ({
      field: {
        onChange,
        name,
        value
      },
      fieldState: {
        error
      }
    }) => {
      return /* @__PURE__ */jsx(InputLabelMessageIconControlled, {
        ...{
          onChange,
          name,
          value,
          placeHolder: name,
          error: !!error,
          ...inputOptions
        }
      });
    }
  });
};
const VerticalRadioGroupInput = (controllerOptions, inputOptions) => {
  return /* @__PURE__ */jsx(Controller, {
    ...controllerOptions,
    render: ({
      field: {
        onChange,
        name,
        value
      },
      fieldState: {
        error
      }
    }) => {
      return /* @__PURE__ */jsx(VerticalRadioGroupControlled, {
        ...{
          onChange,
          name,
          value,
          placeHolder: name,
          ...inputOptions
        }
      });
    }
  });
};
const QuestionGroupCard = props => {
  const {
    questionGroupTitle,
    infoButton,
    children
  } = props;
  const [showInfo, setShowInfo] = useState(false);
  const toggleShowInfo = () => setShowInfo(val => !val);
  const childRender = infoButton ? /* @__PURE__ */jsx(QuestionContextProvider, {
    showInfo,
    children: props.children
  }) : props.children;
  return /* @__PURE__ */jsxs(Card, {
    elevate: true,
    transition: "bouncy",
    hoverStyle: {
      scale: 0.925
    },
    bg: "rgb(224, 243, 228)",
    children: [props.questionGroupTitle ? /* @__PURE__ */jsx(Card.Header, {
      children: /* @__PURE__ */jsxs(Text, {
        children: [props.questionGroupTitle, infoButton ? /* @__PURE__ */jsx(Button, {
          size: "$2",
          icon: Info,
          onPress: toggleShowInfo,
          children: "Learn More"
        }) : null]
      })
    }) : null, /* @__PURE__ */jsx(YStack, {
      alignItems: "stretch",
      padding: 10,
      children: childRender
    })]
  });
};
const QuestionCard = props => {
  const {
    infoButton,
    children
  } = props;
  const [showInfo, setShowInfo] = useState(false);
  const toggleShowInfo = () => setShowInfo(val => !val);
  const childRender = infoButton ? /* @__PURE__ */jsx(QuestionContextProvider, {
    showInfo,
    children: props.children
  }) : props.children;
  return /* @__PURE__ */jsxs(YStack, {
    gap: 10,
    paddingHorizontal: 0,
    children: [props.questionGroupTitle ? /* @__PURE__ */jsxs(YStack, {
      gap: 5,
      children: [/* @__PURE__ */jsx(Text, {
        color: color.grey,
        fontWeight: "600",
        fontSize: 18,
        children: props.questionGroupTitle
      }), infoButton ? /* @__PURE__ */jsx(XStack, {
        children: showInfo ? /* @__PURE__ */jsx(Button, {
          size: "$2",
          iconAfter: ChevronUp,
          children: "Hide",
          onPress: toggleShowInfo
        }) : /* @__PURE__ */jsx(Button, {
          size: "$2",
          icon: Info,
          children: "Learn more",
          onPress: toggleShowInfo
        })
      }) : null]
    }) : null, /* @__PURE__ */jsx(YStack, {
      gap: 15,
      width: "98%",
      height: Platform.select({
        web: "100%",
        native: void 0
      }),
      children: childRender
    })]
  });
};
export { CheckboxInput, CustomCheckboxInput, QuestionCard, QuestionGroupCard, TextInput, VerticalRadioGroupInput, generateIconInput, generateRegisterWithLabel };
//# sourceMappingURL=FormHelpers.mjs.map
